import { createContractCenterRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  DashboardActor,
  DashboardApiResponse,
  DashboardData,
  DashboardDays,
  DashboardExpiringContracts,
  DashboardRecentActivity,
  DashboardTodoGroup,
} from './types/dashboard';

const request = createContractCenterRequest();

const normalizeTodoGroup = (group?: DashboardTodoGroup): DashboardTodoGroup => ({
  total: group?.total ?? 0,
  items: group?.items ?? [],
});

const normalizeActor = (actor?: DashboardActor): DashboardActor => actor ?? null;

const normalizeExpiringContracts = (value: DashboardExpiringContracts, fallbackDays: DashboardDays) => ({
  days: value.days ?? fallbackDays,
  total: value.total ?? 0,
  items: [...(value.items ?? [])].sort((left, right) => left.expirationDate.localeCompare(right.expirationDate)),
}) satisfies DashboardExpiringContracts;

export const normalizeDashboardResponse = (value: DashboardApiResponse): DashboardData => ({
  metrics: {
    totalContracts: value.metrics?.totalContracts ?? 0,
    draftContracts: value.metrics?.draftContracts ?? 0,
    pendingApprovalContracts: value.metrics?.pendingApprovalContracts ?? 0,
    pendingSignContracts: value.metrics?.pendingSignContracts ?? 0,
    activeContracts: value.metrics?.activeContracts ?? 0,
    expiringWithin30Days: value.metrics?.expiringWithin30Days ?? 0,
  },
  myTodos: {
    submittableApprovals: normalizeTodoGroup(value.myTodos?.submittableApprovals),
    pendingSign: normalizeTodoGroup(value.myTodos?.pendingSign),
    pendingFulfillment: normalizeTodoGroup(value.myTodos?.pendingFulfillment),
    expiringSoon: normalizeTodoGroup(value.myTodos?.expiringSoon),
  },
  expiringContracts: {
    ...normalizeExpiringContracts(value.expiringContracts || { days: 30, total: 0, items: [] }, 30),
  },
  statusOverview: value.statusOverview ?? {},
  recentActivities: (value.recentActivities ?? [])
    .slice()
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 10)
    .map((activity: DashboardRecentActivity) => ({
      ...activity,
      operator: normalizeActor(activity.operator),
      member: normalizeActor(activity.member),
    })),
});

export const getDashboard = async (options?: ApiRequestOptions) => {
  const value = await request<DashboardApiResponse>('/bff/dashboard', {
    method: 'GET',
    ...(options || {}),
  });
  return normalizeDashboardResponse(value);
};

export const getExpiringContracts = async (
  days: DashboardDays,
  options?: ApiRequestOptions,
) => {
  const value = await request<DashboardExpiringContracts>('/bff/dashboard/expiring', {
    method: 'GET',
    params: { days },
    ...(options || {}),
  });
  return normalizeExpiringContracts(value, days);
};

export const getDashboardActorName = (
  actor?: DashboardActor,
  lookupMemberName?: (uuid: string) => string | undefined,
): string => {
  if (!actor) return '-';
  if (typeof actor === 'string') return actor;
  const name = actor.name || actor.nickname || actor.username;
  if (name) return name;
  if (actor.memberUuid && lookupMemberName) {
    const memberName = lookupMemberName(actor.memberUuid);
    if (memberName && memberName !== '-') return memberName;
  }
  return '-';
};
